import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Gift, Copy, Check, Users } from "lucide-react";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referral").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const link = `${window.location.origin}/?ref=${data.code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Referral link copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Couldn't copy — select and copy manually.");
    }
  };

  return (
    <div className="rounded-xl border border-accent/30 bg-gradient-to-br from-accent/10 via-card to-card p-6" data-testid="referral-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-accent/20 flex items-center justify-center">
            <Gift className="h-5 w-5 text-accent" />
          </div>
          <div>
            <h3 className="font-display font-bold">Give a friend, get a free month</h3>
            <p className="text-xs text-muted-foreground">Every friend who keeps a paid plan adds 30 days to your access.</p>
          </div>
        </div>
        {data.months_earned > 0 && (
          <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/40" data-testid="referral-months-earned">
            {data.months_earned} free month{data.months_earned > 1 ? "s" : ""} earned
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <input
          data-testid="referral-link-input"
          readOnly
          value={link}
          className="flex-1 min-w-0 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm font-mono text-foreground/80 truncate"
        />
        <Button data-testid="referral-copy-button" onClick={copy} variant="outline" className="border-primary/40 text-primary shrink-0">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>

      <div className="flex items-center gap-6 text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground" data-testid="referral-signups">
          <Users className="h-4 w-4 text-primary" /> <span className="font-mono text-foreground">{data.signups}</span> signed up
        </span>
        <span className="flex items-center gap-1.5 text-muted-foreground" data-testid="referral-conversions">
          <Check className="h-4 w-4 text-emerald-400" /> <span className="font-mono text-foreground">{data.conversions}</span> subscribed
        </span>
      </div>
    </div>
  );
}
